<template>
  <div class="user-list p-4">
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Caricamento...</span>
      </div>
      <div class="loading-text">Caricamento utenti...</div>
    </div>

    <ToastDialog :title="toastTitle" :message="toastMessage" :error="toastError" />

    <!-- Card header con titolo e bottone filtri -->
    <div class="card mb-3 shadow-sm">
      <div class="card-body py-3 px-3">
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <h5 class="mb-0 me-2">Utenti ({{ pagination.total || 0 }})</h5>
            <button class="btn btn-outline-secondary btn-sm p-1" style="border-color: #D3D3D3; font-size: 0.8rem;"
              @click="showFilters = !showFilters">
              <i class="bi bi-funnel-fill"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Filtro utenti -->
    <transition name="slide-fade">
      <UserFilters v-show="showFilters" :all-roles="allRoles" @filter="applyFilter" />
    </transition>

    <!-- Paginazione -->
    <div class="mb-3 d-flex justify-content-between align-items-center">
      <div class="d-flex align-items-center">
        <label class="me-2 mb-0">Elementi per pagina:</label>
        <select class="form-select w-auto" v-model.number="pagination.perPage" @change="loadUsers(1, currentFilters)">
          <option v-for="n in [10, 25, 50, 100, 500]" :key="n" :value="n">{{ n }}</option>
        </select>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary btn-sm rounded-pill px-3 py-2" @click="downloadPdf"
          :disabled="isDownloading || selectedUsers.length === 0">
          <span v-if="!isDownloading">
            <i class="bi bi-file-earmark-pdf me-1"></i> Scarica PDF
          </span>
          <span v-else>
            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Generazione in corso...
          </span>
        </button>
        <button class="btn btn-outline-primary btn-sm rounded-pill px-3 py-2" @click="addUser">
          <i class="bi bi-plus-circle me-1"></i> Aggiungi Utente
        </button>
      </div>
    </div>

    <!-- Tabella utenti -->
    <UserListTable :items="users" :pagination="pagination" :sort-field="sortField" :sort-direction="sortDirection"
      @page-change="loadUsers($event, currentFilters)" @sort="handleSort" @edit-user="editUser"
      @delete-user="deleteUser" @update:selected-users="selectedUsers = $event" @toggle-active="toggleUserActive"
      @reset-password="openResetPassword" @manage-permissions="managePermissions" />


    <ModalView v-model="showUserModal" title="Utente" icon="person">
      <UserForm v-model="selectedUser" :errors="userErrors" :successMessage="userSuccess" @save="saveUser"
        @close="showUserModal = false" />
    </ModalView>

    <ModalView v-model="showPrivilegesModal" :title="privilegesModalTitle" icon="lock">
      <UserPrivilegesForm :key="selectedUserPrivileges.userId" v-model="selectedUserPrivileges"
        :all-privileges="allPrivileges" :roles-with-privileges="rolesWithPrivileges" :errors="privilegesErrors"
        :successMessage="privilegesSuccess" @save="saveUserPrivileges" @close="showPrivilegesModal = false" />
    </ModalView>


    <SimpleModal v-model="showConfirmModal" :title="confirmMessage" :buttons="['Annulla', 'Invia']"
      @chose="handleConfirmChoice" />

    <SimpleModal v-model="showResetModal" :title="resetMessage" :buttons="['Annulla', 'Invia']"
      @chose="handleConfirmReset" />
  </div>
</template>

<script>
import UserListTable from './partials/UserListTable.vue';
import ToastDialog from '../../../components/common/ToastDialog.vue';
import UserFilters from './partials/UserFilters.vue';
import toastable from '../../../mixins/toastable';
import ModalView from '../../../components/common/ModalView.vue';
import UserForm from './partials/UserForm.vue';
import SimpleModal from '../../../components/common/SimpleModal.vue';
import UserPrivilegesForm from './partials/UserPrivilegesForm.vue';


export default {
  name: 'UserListView',
  components: { UserListTable, ToastDialog, UserFilters, UserForm, ModalView, SimpleModal, UserPrivilegesForm },
  mixins: [toastable],
  data() {
    return {
      isLoading: true,
      users: [],
      pagination: { currentPage: 1, perPage: 10, total: 0, lastPage: 1 },
      sortField: 'id',
      sortDirection: 'asc',
      toastTitle: '',
      toastMessage: '',
      toastError: false,
      selectedUsers: [],
      showFilters: true,
      allRoles: [],
      currentFilters: {},
      isDownloading: false,
      showUserModal: false,
      selectedUser: null,
      userErrors: null,
      userSuccess: null,
      showConfirmModal: false,
      userToToggle: null,
      confirmMessage: '',
      showResetModal: false,
      userToReset: null,
      resetMessage: '',
      isResetting: false,
      resetError: '',
      resetSuccess: '',
      showPrivilegesModal: false,
      selectedUserPrivileges: {
        userId: null,
        name: '',
        surname: '',
        privileges: []
      },
      allRolesWithPrivileges: [],
      allPrivileges: [],
      rolesWithPrivileges: [],
      selectedRoleId: null,
      privilegesErrors: null,
      privilegesSuccess: null,
      userToManage: null,
      onlyInactive: false
    };
  },
  created() {
    this.loadUsers(1);
  },
  computed: {
    privilegesModalTitle() {
      if (!this.selectedUserPrivileges) return 'Gestione permessi';

      const { name, surname } = this.selectedUserPrivileges;
      return name && surname
        ? `Gestione permessi ${name} ${surname}`
        : 'Gestione permessi';
    }
  },
  methods: {
    async managePermissions(userId) {
      const user = this.users.find(u => u.id === userId);
      if (!user) return;

      try {
        const res = await this.$root.$api.get(`/admin/${userId}/privileges`);

        this.allPrivileges = res.data.allPrivileges;
        this.rolesWithPrivileges = res.data.roles;

        this.selectedUserPrivileges = {
          userId: userId,
          name: user.name,
          surname: user.surname,
          privileges: res.data.userPrivileges
        };

        this.showPrivilegesModal = true;
      } catch (err) {
        console.error(err);
      }
    },
    async saveUserPrivileges({ userId, privileges }) {
      try {
        this.privilegesErrors = null;
        this.privilegesSuccess = null;

        await this.$root.$api.patch(`/admin/users/${userId}/privileges`, { privileges });

        this.privilegesSuccess = 'Privilegi aggiornati con successo';
        this.showSuccessToast(this.privilegesSuccess);

        this.showPrivilegesModal = false;

        await this.loadUsers(this.pagination.currentPage, this.currentFilters);
      } catch (err) {
        console.error('Errore salvataggio privilegi', err);
        this.privilegesErrors = err.response?.data?.errors || { general: ['Errore durante il salvataggio'] };
        this.showErrorToast('Errore durante il salvataggio dei privilegi');
      }
    },
    async handleConfirmReset(choice) {
      this.showResetModal = false;

      if (choice !== 'Invia' || !this.userToReset) return;

      try {
        this.isResetting = true;
        this.resetError = '';
        this.resetSuccess = '';

        const response = await this.$root.$authService.forgotPassword(this.userToReset.email);

        this.resetSuccess = 'Email inviata con successo';
        this.showSuccessToast(this.resetSuccess);

      } catch (err) {
        this.resetError = err.response?.data?.errors?.email
          ? err.response.data.errors.email[0]
          : "Si è verificato un errore durante l'invio dell'email";

        this.showErrorToast(this.resetError);
      } finally {
        this.isResetting = false;
        this.userToReset = null;
      }
    },
    openResetPassword(userId) {
      const user = this.users.find(u => u.id === userId);

      if (!user) {
        console.error('Utente non trovato', userId);
        return;
      }

      this.userToReset = user;
      this.resetMessage = `Vuoi inviare il link di reset password per ${user.name} ${user.surname}?`;
      this.resetError = '';
      this.resetSuccess = '';
      this.showResetModal = true;
    },
    toggleUserActive(userId) {
      const user = this.users.find(u => u.id === userId);

      if (!user) {
        console.error('Utente non trovato', userId);
        return;
      }

      this.userToToggle = user;

      const action = user.active ? 'disabilitare' : 'attivare';
      this.confirmMessage = `Vuoi ${action} ${user.name} ${user.surname}?`;

      this.showConfirmModal = true;
    },
    async handleConfirmChoice(choice) {
      this.showConfirmModal = false;

      if (choice !== 'Invia' || !this.userToToggle) return;

      try {
        const response = await this.$root.$api.patch(
          `/admin/${this.userToToggle.id}/toggle-active`
        );

        this.userToToggle.active = response.data.active;

        this.showSuccessToast(
          this.userToToggle.active
            ? 'Utente attivato con successo'
            : 'Utente disabilitato con successo'
        );

      } catch (error) {
        console.error('Errore toggle active', error);
        this.showErrorToast('Errore durante l\'aggiornamento dello stato utente');
      } finally {
        this.userToToggle = null;
      }
    },
    addUser() {
      this.selectedUser = {
        id: null,
        name: '',
        surname: '',
        email: '',
        fiscalcode: '',
        organization: '',
        organization_fiscal_code: '',
        user_type_code: 'B'
      };
      this.userErrors = null;
      this.userSuccess = null;
      this.showUserModal = true;
    },
    applyFilter(filters) {
      this.currentFilters = { ...filters };
      this.pagination.currentPage = 1;
      this.loadUsers(1, this.currentFilters);
    },

    async loadUsers(page = 1, filters = {}) {
      this.isLoading = true;
      try {
        const params = {
          page,
          per_page: this.pagination.perPage,
          sortField: this.sortField,
          sortDirection: this.sortDirection,
          name: filters.name || '',
          surname: filters.surname || '',
          role_ids: filters.role_ids || '',
          onlyInactive: filters.onlyInactive ? 1 : 0
        };
        const res = await this.$root.$api.get('/admin/users', { params });

        this.users = res.data.data;
        this.pagination = {
          currentPage: res.data.current_page,
          perPage: res.data.per_page,
          total: res.data.total,
          lastPage: res.data.last_page
        };

        if (!this.allRoles.length) {
          const allRolesMap = {};
          this.users.flatMap(u => u.roles || []).forEach(r => {
            if (!allRolesMap[r.id]) {
              allRolesMap[r.id] = { id: r.id, description: r.description };
            }
          });
          this.allRoles = Object.values(allRolesMap);
        }

      } catch (e) {
        this.toastTitle = 'Errore';
        this.toastMessage = 'Errore nel caricamento degli utenti';
        this.toastError = true;
      } finally {
        this.isLoading = false;
      }
    },

    handleSort({ field, direction }) {
      this.sortField = field;
      this.sortDirection = direction;
      this.loadUsers(1, this.currentFilters);
    },

    editUser(id) {
      this.userErrors = null;
      this.userSuccess = null;

      const user = this.users.find(u => u.id === id);
      if (user) {
        this.selectedUser = {
          id: user.id || null,
          username: user.username || '',
          name: user.name || '',
          surname: user.surname || '',
          email: user.email || '',
          fiscalcode: user.fiscalcode || '',
          organization: user.organization || '',
          organization_fiscal_code: user.organization_fiscal_code || '',
          user_type_code: user.user_type_code || 'B'
        };
        this.showUserModal = true;
      }
    },
    deleteUser(id) {
      console.log('Delete user', id);
    },
    async downloadPdf() {
      this.isDownloading = true;
      try {

        const idsToPrint = this.selectedUsers.length > 0
          ? this.selectedUsers
          : [];

        const params = {
          ids: idsToPrint.join(','),
          sortField: this.sortField,
          sortDirection: this.sortDirection,

          name: this.currentFilters.name || '',
          surname: this.currentFilters.surname || '',
          role_ids: (this.currentFilters.role_ids || []).join(','),
          //onlyInactive: this.currentFilters.onlyInactive || false
        };

        await this.$root.$api.get('/admin/users/pdf/export', { params });

        this.showSuccessToast('PDF in preparazione. Ti verrà notificato quando sarà pronto.');
        this.$root.triggerPdfPolling();

      } catch (error) {
        this.toastTitle = 'Errore';
        this.toastMessage = 'Impossibile scaricare il PDF';
        this.toastError = true;
      } finally {
        this.isDownloading = false;
      }
    },
    async saveUser(userData) {
      this.isLoading = true;
      try {
        if (userData.id) {
          await this.$root.$api.put(`/admin/users/${userData.id}`, userData);
          this.userSuccess = 'Utente aggiornato con successo';
        } else {
          const res = await this.$root.$api.post('/admin/users', userData);
          this.userSuccess = 'Utente creato con successo';

          const newUser = res.data.data;
          if (newUser) {
            this.users.push(newUser);
          }
        }

        this.showUserModal = false;
        await this.loadUsers(this.pagination.currentPage, this.currentFilters);
        this.showSuccessToast(this.userSuccess);

      } catch (err) {
        this.userErrors = err.response?.data?.errors || { general: ['Errore durante il salvataggio'] };
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.loading-text {
  margin-top: 1rem;
  font-weight: bold;
  color: #0d6efd;
}
</style>
